import { trailOptionLabel, type RouteResult } from "../engine/planner";

export const km = (m: number) => `${(m / 1000).toFixed(m < 10_000 ? 2 : 1)} km`;
export const metres = (m: number) => `${Math.round(m)} m`;
export const pct = (x: number) => `${Math.round(x * 100)}%`;

export function trailHint(v: number): string {
  if (v <= -1) return "Roads & pavement";
  if (v < 0) return "Mostly paved";
  if (v === 0) return "Mixed";
  if (v < 1) return "Mostly trails";
  return "Trails & dirt";
}

export function shapeName(shape: string, loop: boolean): string {
  if (shape === "direct") return "Direct";
  if (shape.startsWith("arc")) return "Curved route";
  if (shape.startsWith("lobe-start")) return "Loop first, then go";
  if (shape.startsWith("lobe-end")) return "Go, then loop";
  return loop ? "Loop" : "Route";
}

// ---- Running trails (ADR 0006) ----------------------------------------------------------------

/** "42% on trails" for the meta line; null when trails were not used or the share rounds to 0. */
export function trailLine(share: number, state: string): string | null {
  return state === "used" && Math.round(share * 100) > 0 ? `${pct(share)} on trails` : null;
}

/** "8 km", "3.5 km". */
export const targetKm = (m: number) => `${Number((m / 1000).toFixed(1))} km`;

/** "8% short of 8 km" or "8% over 8 km"; null when within 2% of the target. */
export function distanceNote(lengthM: number, targetM: number): string | null {
  const dev = (lengthM - targetM) / targetM;
  const p = Math.round(Math.abs(dev) * 100);
  if (p < 2) return null;
  return `${p}% ${dev < 0 ? "short of" : "over"} ${targetKm(targetM)}`;
}

/**
 * Second meta line: how far a trail option (not a "Via X" route) is off the target distance, then
 * the other named trails on the route (the option's own name is already the title).
 */
export function routeNote(r: RouteResult, targetM: number): string | null {
  const parts: string[] = [];
  if (r.trailOption && !r.trailOption.via) {
    const d = distanceNote(r.lengthM, targetM);
    if (d) parts.push(d);
  }
  parts.push(...r.trailNames.filter((n) => n !== r.trailOption?.name));
  return parts.length ? parts.join(" · ") : null;
}

/**
 * The About credit's date suffix for the trails of the current area: ", data from YYYY-MM-DD", or
 * "" when the area has no trails (`generated` null) so an earlier area's date does not linger.
 */
export function trailsCredit(generated: string | null): string {
  const d = generated?.slice(0, 10) ?? "";
  return /^\d{4}-\d\d-\d\d$/.test(d) ? `, data from ${d}` : "";
}

/** Identity of a route's geometry, so a re-plan that changes nothing on the map is not redrawn. */
export function routeKey(r: RouteResult): string {
  let h = 2166136261;
  for (const e of r.edges) h = Math.imul(h ^ e, 16777619) >>> 0;
  return `${r.edges.length}:${h}:${Math.round(r.lengthM)}:${r.shape}`;
}

export function sameRoutes(a: RouteResult[], b: RouteResult[]): boolean {
  return a.length === b.length && a.every((r, i) => routeKey(r) === routeKey(b[i] as RouteResult));
}

/** Everything a card shows, so cards are rebuilt only when their text would change. */
export function cardsKey(routes: RouteResult[], context: string): string {
  return [
    context,
    ...routes.map((r) =>
      [
        routeKey(r),
        Math.round(r.climbM),
        Math.round(r.green * 100),
        Math.round(r.popularShare * 100),
        r.newShare === undefined ? "" : Math.round(r.newShare * 100),
        Math.round(r.trailShare * 100),
        r.trailNames.join("|"),
        r.trailOption ? trailOptionLabel(r.trailOption) : "",
      ].join(","),
    ),
  ].join(";");
}
